import React, { useEffect, useRef, useState } from "react";

import { Helmet } from "react-helmet-async";
import { apiBaseUrl,baseUrl } from '../../config';
import { Link } from 'react-router-dom';
import { useInView } from "react-intersection-observer";
import axios from 'axios';
import parse from "html-react-parser";


import dcs from "./../../images/dcs.png";

import "owl.carousel/dist/assets/owl.carousel.css";
import "owl.carousel/dist/assets/owl.theme.default.css";

const MainPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const isCarouselInitialized = useRef(false);
  const isDoctorCarouselInitialized = useRef(false);
  const [isCarouselReady, setIsCarouselReady] = useState(false);
  const [activeAccordion, setActiveAccordion] = useState("collapseOne");
  const [cms, setCms] = useState([]);
  
  // Dynamic data states
  const [philosophies, setPhilosophies] = useState([]);
  const [services, setServices] = useState([]);
  const [products, setProducts] = useState([]);
  const [steps, setSteps] = useState([]);
  const [trajectories, setTrajectories] = useState([]);
  const [dataItems, setDataItems] = useState([]);
  const [features, setFeatures] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [accordionItems, setAccordionItems] = useState([]);

  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.3,
  });

  // Fetch CMS
  const fetchCms = async () => {
    setLoading(true);
    setError(null);
  
    try {
      const { data } = await axios.get(`${apiBaseUrl}/all-get-cms`);
  
      if (data.status) {
        const cmsData = {};
        data.cms_pages.forEach((item) => {
          cmsData[item.cms_id] = item;
        });
        setCms(cmsData);
      } else {
        setCms({});
        setError(data.message || "Failed to fetch CMS pages");
      }
    } catch (err) {
      console.error("Error fetching CMS pages:", err);
      setCms({});
      setError(err.response?.data?.message || "Failed to load CMS pages. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Fetch all items from database
  const fetchItems = async () => {
    try {
      const response = await axios.get(`${apiBaseUrl}/items`, {
        params: { limit: 100 }
      });
      
      if (response.data.status) {
        const allItems = response.data.data;
        setPhilosophies(allItems.filter(item => item.type === 'philosophy'));
        setServices(allItems.filter(item => item.type === 'service'));
        setProducts(allItems.filter(item => item.type === 'product'));
        setSteps(allItems.filter(item => item.type === 'step'));
        setTrajectories(allItems.filter(item => item.type === 'trajectory'));
        setDataItems(allItems.filter(item => item.type === 'data'));
        setFeatures(allItems.filter(item => item.type === 'feature'));
        setAccordionItems(allItems.filter(item => item.type === 'accordion'));
      }
    } catch (error) {
      console.error('Error fetching items:', error);
    }
  };

  // Fetch doctors
  const fetchDoctors = async () => {
    try {
      const response = await axios.get(`${apiBaseUrl}/doctors`, {
        params: { limit: 10, status: 1 }
      });
      
      if (response.data.status) {
        setDoctors(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching doctors:', error);
      // Set default doctors if API fails
      setDoctors([
        {
          id: 1,
          name: "Dr. John Smith",
          specialization: "Cardiologist",
          experience_years: 12,
          image: "https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?w=400",
        },
        {
          id: 2,
          name: "Dr. Sarah Wilson",
          specialization: "Neurologist",
          experience_years: 10,
          image: "https://images.unsplash.com/photo-1594824476967-48c8b964273f?w=400",
        },
        {
          id: 3,
          name: "Dr. Michael Lee",
          specialization: "Dentist",
          experience_years: 8,
          image: "https://images.unsplash.com/photo-1622253692010-333f2da6031d?w=400",
        },
        {
          id: 4,
          name: "Dr. Emma Brown",
          specialization: "Dermatologist",
          experience_years: 15,
          image: "https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=400",
        },
        {
          id: 5,
          name: "Dr. David Miller",
          specialization: "Orthopedic",
          experience_years: 14,
          image: "https://images.unsplash.com/photo-1537368910025-700350fe46c7?w=400",
        },
      ]);
    }
  };

  useEffect(() => {
    fetchCms();
    fetchItems();
    fetchDoctors();
  }, []);

  // Load carousel
  useEffect(() => {
    const loadCarousel = async () => {
      try {
        const jQueryModule = await import("jquery");
        const $ = jQueryModule.default || jQueryModule;
        window.jQuery = $;
        window.$ = $;
        await import("owl.carousel");
        setIsCarouselReady(true);
        setTimeout(() => {
          initializeCarousels();
        }, 500);
      } catch (error) {
        console.error("Error loading carousel:", error);
        setIsCarouselReady(false);
      }
    };

    const initializeCarousels = () => {
      const $ = window.$;
      if (!$ || !$.fn || !$.fn.owlCarousel) {
        console.error("jQuery or owlCarousel not available");
        return;
      }

      if (!isCarouselInitialized.current) {
        const testimonialElement = $(".testimonial-slider");
        if (testimonialElement.length) {
          try {
            testimonialElement.owlCarousel({
              items: 1,
              loop: true,
              autoplay: true,
              autoplayTimeout: 4000,
              smartSpeed: 800,
              dots: true,
              nav: false,
              margin: 30,
            });
            isCarouselInitialized.current = true;
          } catch (e) {
            console.error("Error initializing testimonial carousel:", e);
          }
        }
      }

      if (!isDoctorCarouselInitialized.current) {
        const doctorElement = $(".doctor-slider");
        if (doctorElement.length) {
          try {
            doctorElement.owlCarousel({
              loop: true,
              margin: 25,
              nav: false,
              dots: false,
              autoplay: true,
              autoplayTimeout: 3000,
              smartSpeed: 800,
              responsive: {
                0: { items: 1 },
                576: { items: 2 },
                992: { items: 3 },
                1200: { items: 4 },
              },
            });
            isDoctorCarouselInitialized.current = true;
          } catch (e) {
            console.error("Error initializing doctor carousel:", e);
          }
        }
      }
    };

    loadCarousel();

    return () => {
      const $ = window.$;
      if ($ && $.fn && $.fn.owlCarousel) {
        try {
          if (isCarouselInitialized.current) {
            $(".testimonial-slider").owlCarousel("destroy");
            isCarouselInitialized.current = false;
          }
          if (isDoctorCarouselInitialized.current) {
            $(".doctor-slider").owlCarousel("destroy");
            isDoctorCarouselInitialized.current = false;
          }
        } catch (e) {
          console.log("Carousel already destroyed or not initialized");
        }
      }
    };
  }, [philosophies, services, products, steps, trajectories, dataItems, features, doctors]);

  const toggleAccordion = (id) => {
    setActiveAccordion(activeAccordion === id ? null : id);
  };

  // Render doctor cards
  const renderDoctorCards = () => {
    return doctors.map((doctor) => (
      <div className="item" key={doctor.id}>
        <div className="doctor-card">
          <img 
            src={doctor.image ? (doctor.image.startsWith('http') ? doctor.image : apiBaseUrl + '/uploads/doctors/' + doctor.image) : '/default-doctor.jpg'} 
            alt={doctor.name} 
            className="doctor-img" 
          />
          <div className="doctor-body">
            <h4>{doctor.name}</h4>
            <span className="speciality">{doctor.specialization}</span>
            <p>{doctor.experience_years || 0} Years Experience</p>
            <button className="appointment-btn">Book Appointment</button>
          </div>
        </div>
      </div>
    ));
  };

  // Render accordion items
  const renderAccordion = () => {
    return accordionItems.map((item, index) => {
      const accordionId = `collapse${index + 1}`;
      const isActive = activeAccordion === accordionId;
      
      return (
        <div className="accordion-item" key={index}>
          <h2 className="accordion-header" id={`heading${index + 1}`}>
            <button
              className={`accordion-button ${isActive ? '' : 'collapsed'}`}
              type="button"
              onClick={() => toggleAccordion(accordionId)}
              aria-expanded={isActive}
              aria-controls={accordionId}
            >
              {item.icon && <i className={`fas ${`fas ${item.icon}`} me-2`}></i>}
              {item.title}
            </button>
          </h2>
          <div
            id={accordionId}
            className={`accordion-collapse collapse ${isActive ? 'show' : ''}`}
            aria-labelledby={`heading${index + 1}`}
          >
            <div className="accordion-body">
              {item.description}
            </div>
          </div>
        </div>
      );
    });
  };

  return (
    <div>
      <Helmet>
        <title>Predyx</title>
      </Helmet>

      {/* ============================================ */}
      {/* HERO SECTION - CMS ID 97 */}
      {/* ============================================ */}
      <section className="hero-section">
        <div className="hdsd">
          <div className="container">
            <h3>{cms[97]?.cms_page_heading}</h3>
            {parse(cms[97]?.cms_page_description || "")}
            <Link to={baseUrl + '/contact'} onClick={() => window.scrollTo(0, 0)}  className="action-items-primary-btn">
              Request a Demo
              <i className="fas fa-chevron-right" />
            </Link>
            <Link to={baseUrl + '/clinical-intelligence'}  onClick={() => window.scrollTo(0, 0)} className="action-items-primary-btn1">
              Explore Clinical Intelligence
              <i className="fas fa-chevron-right" />
            </Link>
            <h3 style={{ color: "#fff", marginTop: "30px", fontSize: "20px" }}>
              {cms[97]?.cms_page_small_description} →
            </h3>
          </div>
        </div>
        <img src={`${apiBaseUrl}/assets/images/${cms[97]?.cms_image}`} alt="About us" style={{width: "100%" }} className="img-fluid" />
      </section>

      {/* ============================================ */}
      {/* CATEGORY SECTION - CMS ID 98, 99, 100 */}
      {/* ============================================ */}
      <section className="category-section">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-10 text-center">
              <h2 className="section-title">{parse(cms[98]?.cms_page_heading || "")}</h2>
              <p className="subtitle">{cms[98]?.cms_page_small_description}</p>
            </div>
          </div>
          <div className="row">
            <div className="col-lg-12">
              <div className="highlight-box">
                <h4>{cms[99]?.cms_page_heading}</h4>
                {parse(cms[99]?.cms_page_description || "")}
              </div>
            </div>
          </div>
          <div className="row process text-center">
            <div className="col-lg-2 col-md-4">
              <div className="step">
                <i className="fas fa-heartbeat" />
                <h5>Clinical<br />Signals</h5>
                <div className="arrow"><i className="fas fa-chevron-right" /></div>
              </div>
            </div>
            <div className="col-lg-2 col-md-4">
              <div className="step">
                <i className="fas fa-project-diagram" />
                <h5>Biological<br />Relationships</h5>
                <div className="arrow"><i className="fas fa-chevron-right" /></div>
              </div>
            </div>
            <div className="col-lg-2 col-md-4">
              <div className="step">
                <i className="fas fa-brain" />
                <h5>Latent<br />State Mapping</h5>
                <div className="arrow"><i className="fas fa-chevron-right" /></div>
              </div>
            </div>
            <div className="col-lg-2 col-md-4">
              <div className="step">
                <i className="fas fa-dna" />
                <h5>Organ<br />Trajectories</h5>
                <div className="arrow"><i className="fas fa-chevron-right" /></div>
              </div>
            </div>
            <div className="col-lg-2 col-md-4">
              <div className="step">
                <i className="fas fa-chart-line" />
                <h5>Clinical<br />Intelligence</h5>
                <div className="arrow"><i className="fas fa-chevron-right" /></div>
              </div>
            </div>
            <div className="col-lg-2 col-md-4">
              <div className="step">
                <i className="fas fa-user-md" />
                <h5>Physician<br />Action</h5>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-lg-12">
              <div className="quote-box">
                <i className="fas fa-quote-left" />
                <p>{parse(cms[100]?.cms_page_heading || "")}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* WHAT PREDYXIQ IS - CMS ID 101 */}
      {/* ============================================ */}
      <section className="predyxiq-section py-5">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="display-title">What <span>Predyx</span> is</h2>
            <h5 className="mini-title">{parse(cms[101]?.cms_page_heading || "")}</h5>
            <h3 className="main-title">{parse(cms[101]?.cms_page_small_description || "")}</h3>
            <p className="intro-text mt-4">{parse(cms[101]?.cms_page_description || "")}</p>
          </div>
          <div className="row">
            {features.map((item, index) => (
              <div className="col-lg-4 mb-4" key={index}>
                <div className="feature-card">
                  <div className="icon-box">
                    <i className={`fas ${item.icon}`}></i>
                  </div>
                  <h4>{item.title}</h4>
                  <p>{item.description || item.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* WHAT PREDYXIQ DOES - CMS ID 102 */}
      {/* ============================================ */}
      <section className="does-section">
        <div className="container">
          <div className="text-center mb-2">
            <h2 className="section-title">{parse(cms[102]?.cms_page_heading || "")}</h2>
            <h3 className="section-subtitle">{parse(cms[102]?.cms_page_small_description || "")}</h3>
            <p className="section-text">{parse(cms[102]?.cms_page_description || "")}</p>
          </div>
          <div className="row align-items-center">
            <div className="col-lg-7">
              <div className="timeline">
                {dataItems.map((item, index) => (
                  <div className="timeline-item" key={index}>
                    <div className="number">{index + 1}</div>
                    <div className="content">
                      <h5>{item.title}</h5>
                      <p>{item.description || item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="col-lg-5 text-center">
              <img
                src={`${apiBaseUrl}/assets/images/${cms[102]?.cms_image}`}
                className="img-fluid side-image"
                alt="Medical illustration"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* ORGAN TRAJECTORIES - CMS ID 103, 104 */}
      {/* ============================================ */}
      <section className="trajectory-section">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="section-title">{parse(cms[103]?.cms_page_heading || "")}</h2>
            <h5 className="section-subtitle">{parse(cms[103]?.cms_page_small_description || "")}</h5>
            <h3 className="main-heading">{parse(cms[103]?.cms_page_small_description_second || "")}</h3>
            <p className="lead-text mt-2">{parse(cms[103]?.cms_page_description || "")}</p>
          </div>

          <div className="row">
            {trajectories.map((item, index) => (
              <div className="col-lg-6 mb-4" key={index}>
                <div className="trajectory-card d-flex">
                  <div className="icon-circle">
                    <i className={`fas ${item.icon}`}></i>
                  </div>
                  <div className="card-content">
                    <h4>{item.title} Trajectory</h4>
                    <p>{item.description || item.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="note-box mt-3">
            <i className="fas fa-info-circle mr-2"></i>
            {parse(cms[104]?.cms_page_heading || "")}
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* EXPLAINABILITY - CMS ID 105, 106, 107, 108, 109 */}
      {/* ============================================ */}
      <section className="explain-section">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-10 text-center">
              <h2 className="section-title">{parse(cms[105]?.cms_page_heading || "")}</h2>
              <p className="section-tag">{parse(cms[105]?.cms_page_small_description || "")}</p>
              <h3 className="hero-title">{parse(cms[105]?.cms_page_small_description_second || "")}</h3>
              <p className="section-text">{parse(cms[105]?.cms_page_description || "")}</p>
            </div>
          </div>

          <div className="decision-card">
            <div className="decision-header">
              <div className="status-dot"></div>
              <h4>{parse(cms[106]?.cms_page_heading || "")}</h4>
              <span>{parse(cms[106]?.cms_page_small_description || "")}</span>
            </div>

            <div className="row">
              <div className="col-lg-7">
                <div className="info-card">
                  <h5>{parse(cms[107]?.cms_page_heading || "Evidence Used")}</h5>
                  {parse(cms[107]?.cms_page_description || "")}
                </div>
              </div>

              <div className="col-lg-5">
                <div className="analysis-card">
                  <div className="analysis-block">
                    <h6>{parse(cms[108]?.cms_page_heading || "Clinical Interpretation")}</h6>
                    <p>{parse(cms[108]?.cms_page_small_description || "")}</p>
                  </div>
                  <div className="analysis-block">
                    <h6>{parse(cms[109]?.cms_page_heading || "Suggested Action")}</h6>
                    <p>{parse(cms[109]?.cms_page_small_description || "")}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* PHYSICIAN PHILOSOPHY - CMS ID 110, 111 */}
      {/* ============================================ */}
      <section className="physician-section">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="section-title">{parse(cms[110]?.cms_page_heading || "Physician-First <span>Philosophy</span>")}</h2>
            <h3 className="section-subtitle">{parse(cms[110]?.cms_page_small_description || "Technology should empower physicians, <strong> not replace them.</strong>")}</h3>
            <p className="section-text">{parse(cms[110]?.cms_page_description || "")}</p>
          </div>

          <div className="row align-items-center philosophy-wrapper">
            <div className="col-lg-5">
              <div className="philosophy-card right-card">
                <h4>Physician Expertise</h4>
                <ul>
                  <li>Clinical judgment</li>
                  <li>Patient context & empathy</li>
                  <li>Medical decision making</li>
                  <li>Treatment responsibility</li>
                </ul>
              </div>
            </div>
            <div className="col-lg-2 text-center">
              <div className="center-circle">
                <i className="fas fa-handshake"></i>
              </div>
            </div>
            <div className="col-lg-5">
              <div className="philosophy-card left-card">
                <h4>Predyx Intelligence</h4>
                <ul>
                  <li>Connects fragmented clinical signals</li>
                  <li>Recognizes biological relationships</li>
                  <li>Provides explainable clinical reasoning</li>
                  <li>Highlights meaningful trajectories</li>
                </ul>
              </div>
            </div>
          </div>

          <div className="quote-box">
            <i className="fas fa-quote-left"></i>
            <h4>{parse(cms[111]?.cms_page_heading || "Predyx supports the decision. <span> The physician makes it.</span>")}</h4>
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* PRODUCTS - Items (type: product) + CMS ID 112 */}
      {/* ============================================ */}
      <section className="physician-section py-5" style={{ background: "linear-gradient(135deg, #143b63, #1d5f8c)" }}>
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="section-title" style={{ color: "#fff" }}>
              {parse(cms[112]?.cms_page_heading || "Clinical Intelligence Tailored <span style='color:#33dcff;'>to Every Level of Care</span>")}
            </h2>
          </div>

          <div className="row">
            {products.map((item, index) => (
              <div className="col-md-6 mb-4" key={index}>
                <div className="card h-100 border-0 shadow-sm" style={{ transition: "0.3s", borderRadius: "12px" }}>
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-start mb-3">
                      <h5 className="font-weight-bold text-dark mb-0">{item.title}</h5>
                      <span className={`badge ${item.badge}`}>{item.status}</span>
                    </div>
                    <p className="text-secondary mb-0">{item.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* HOW IT WORKS - Items (type: step) + CMS ID 113 */}
      {/* ============================================ */}
      <section className="does-section works py-5" style={{ backgroundColor: "#eef3f8" }}>
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="section-title">{parse(cms[113]?.cms_page_heading || "How <span>It</span> Works")}</h2>
            <p className="section-text">{cms[113]?.cms_page_small_description || "From Health Data to Clinical Intelligence"}</p>
          </div>
          <div className="row justify-content-center">
            <div className="col-lg-6 pr-3">
              {steps.map((step, index) => (
                <div key={index} className="d-flex mb-4 p-3 bg-white shadow-sm rounded">
                  <div className="mr-3 text-white font-weight-bold" style={{
                    width: "42px", height: "42px", background: "#34485f",
                    borderRadius: "50%", display: "flex", alignItems: "center",
                    justifyContent: "center", flexShrink: 0,
                  }}>
                    {index + 1}
                  </div>
                  <div>
                    <h5 className="font-weight-bold mb-1">{step.title}</h5>
                    <p className="text-muted mb-0">{step.description || step.text}</p>
                  </div>
                  <div className="predyx4-arrow">&#8595;</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* WHO WE SERVE - Items (type: service) + CMS ID 114 */}
      {/* ============================================ */}
      <section className="does-section who-we-serve-section">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="section-title">{parse(cms[114]?.cms_page_heading || "Who <span>We</span> Serve")}</h2>
            <p className="section-text">{cms[114]?.cms_page_small_description || "Empowering Every Level of Healthcare"}</p>
          </div>

          <div className="row">
            {services.map((item, index) => (
              <div className="col-md-6 col-lg-4 mb-4" key={index}>
                <div className="serve-card">
                  <div className="serve-icon">
                    <i className={`fas ${item.icon}`}></i>
                  </div>
                  <h5>{item.title}</h5>
                  <p>{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* SCIENCE & GOVERNANCE - CMS ID 115, 116, 117 */}
      {/* ============================================ */}
      <section className="sg-section py-5">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-7 mb-4 mb-lg-0 pr-4">
              <h2 className="sg-title">{parse(cms[115]?.cms_page_heading || "Science <span style='color:#4a93d9;'>& Governance</span>")}</h2>
             <div className="vcs"> {parse(cms[117]?.cms_page_description || "")}</div>
         
            </div>

            <div className="col-lg-5">
              <div className="sg-image-card">
                <img src={dcs} alt="How It Works" className="img-fluid" />
                <div className="sg-image-overlay">
                  <div className="sg-badge">✔ Transparent</div>
                  <div className="sg-badge">✔ Evidence-Based</div>
                  <div className="sg-badge">✔ Reproducible</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* FOUNDING PHILOSOPHY - Items (type: philosophy) + CMS ID 118 */}
      {/* ============================================ */}
      <section className="fp-section py-5">
        <div className="container">
          <div className="text-center mb-5">
            <h2 className="fp-title">{parse(cms[118]?.cms_page_heading || "Founding <span>Philosophy</span>")}</h2>
            <p className="fp-subtitle">{cms[118]?.cms_page_small_description || "Guiding principles that define the scientific foundation and philosophy behind the platform."}</p>
          </div>

          <div className="row">
            {philosophies.map((item, index) => (
              <div className="col-lg-6 mb-4" key={index}>
                <div className="fp-card">
                  <div className="fp-icon">
                    <i className={`fas ${item.icon}`}></i>
                  </div>
                  <div className="fp-content">
                    <h4 className="fp-card-title">{item.title}</h4>
                    <p className="fp-card-text mb-0">{item.description || item.text}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================================ */}
      {/* FOOTER CTA - CMS ID 119 */}
      {/* ============================================ */}
      <section className="funfacts" ref={ref}>
        <div className="overlay">
          <div className="container" style={{ textAlign: "center" }}>
            <h2 style={{ fontSize: "35px", color: "#fff", fontWeight: "600" }}>
              {parse(cms[119]?.cms_page_heading || '"We cannot change yesterday\'s biology.<br />We can still change tomorrow\'s."')}
            </h2>
            <p style={{ fontSize: "16px", color: "#fff", marginBottom: "30px" }}>
              {cms[119]?.cms_page_small_description || "Discover how explainable Clinical Intelligence can strengthen preventive healthcare - from one clinical encounter to an entire population."}
            </p>
            <Link to={baseUrl + '/contact'} onClick={() => window.scrollTo(0, 0)}   className="action-items-primary-btn1">
              Request a Demo
              <i className="fas fa-chevron-right" />
            </Link>
            <Link to={baseUrl + '/predyxiq-quick'} onClick={() => window.scrollTo(0, 0)}  className="action-items-primary-btn1">
              Explore predyxiq IQ Quick
              <i className="fas fa-chevron-right" />
            </Link>
          
          </div>
        </div>
      </section>
    </div>
  );
};

export default MainPage;